import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Loader2, Type, AlignLeft, List, Image as ImageIcon, MousePointerClick, History } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { StatusChip, Btn, ConfirmDialog, Modal, Field, inputCls, MediaPicker } from "../components";

const BLOCK_META = {
  heading: { label: "Heading", icon: Type },
  paragraph: { label: "Paragraph", icon: AlignLeft },
  list: { label: "List", icon: List },
  image: { label: "Image", icon: ImageIcon },
  button: { label: "Button / Link", icon: MousePointerClick },
};

const newBlock = (type) =>
  type === "heading" ? { type, text: "" }
  : type === "paragraph" ? { type, text: "" }
  : type === "list" ? { type, items: [""] }
  : type === "image" ? { type, src: "", caption: "", alt: "" }
  : { type, label: "", href: "" };

const BlockEditor = ({ block, index, onChange, onMove, onRemove, openPicker }) => {
  const Meta = BLOCK_META[block.type];
  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50 p-4" data-testid={`block-${index}-${block.type}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500"><Meta.icon size={13} className="text-[#C5221F]" /> {Meta.label}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(index, -1)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`block-${index}-up`}><ArrowUp size={14} /></button>
          <button type="button" onClick={() => onMove(index, 1)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`block-${index}-down`}><ArrowDown size={14} /></button>
          <button type="button" onClick={() => onRemove(index)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`block-${index}-remove`}><Trash2 size={14} /></button>
        </div>
      </div>
      {block.type === "heading" && <input className={inputCls} placeholder="Heading text" value={block.text} onChange={(e) => onChange(index, { ...block, text: e.target.value })} data-testid={`block-${index}-text`} />}
      {block.type === "paragraph" && <textarea rows={4} className={inputCls} placeholder="Paragraph text…" value={block.text} onChange={(e) => onChange(index, { ...block, text: e.target.value })} data-testid={`block-${index}-text`} />}
      {block.type === "list" && <textarea rows={4} className={inputCls} placeholder={"One item per line"} value={(block.items || []).join("\n")} onChange={(e) => onChange(index, { ...block, items: e.target.value.split("\n") })} data-testid={`block-${index}-items`} />}
      {block.type === "image" && (
        <div className="space-y-3">
          {block.src ? (
            <div className="flex items-center gap-3">
              <img src={block.src} alt="" className="w-32 h-20 object-cover border border-slate-200" />
              <Btn variant="ghost" type="button" onClick={() => openPicker(index)}>Change</Btn>
            </div>
          ) : (
            <Btn variant="ghost" type="button" onClick={() => openPicker(index)} data-testid={`block-${index}-pick-image`}>Choose Image</Btn>
          )}
          <input className={inputCls} placeholder="Alt text (for accessibility & SEO)" value={block.alt} onChange={(e) => onChange(index, { ...block, alt: e.target.value })} />
          <input className={inputCls} placeholder="Caption (optional)" value={block.caption} onChange={(e) => onChange(index, { ...block, caption: e.target.value })} />
        </div>
      )}
      {block.type === "button" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Button label" value={block.label} onChange={(e) => onChange(index, { ...block, label: e.target.value })} data-testid={`block-${index}-label`} />
          <input className={inputCls} placeholder="Link, e.g. /contact" value={block.href} onChange={(e) => onChange(index, { ...block, href: e.target.value })} data-testid={`block-${index}-href`} />
        </div>
      )}
    </div>
  );
};

const PageEditor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [pickerIdx, setPickerIdx] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);

  useEffect(() => {
    adminApi.get(`/pages/${id}`)
      .then((r) => { setDoc(r.data); setData(r.data.data); })
      .catch((e) => { toast.error(fmtErr(e)); navigate("/admin/pages"); });
  }, [id, navigate]);

  if (!doc || !data) return <div className="py-20 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;

  const set = (k, v) => setData({ ...data, [k]: v });
  const setBlock = (i, b) => set("blocks", data.blocks.map((x, k) => (k === i ? b : x)));
  const moveBlock = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= data.blocks.length) return;
    const blocks = [...data.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    set("blocks", blocks);
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await adminApi.put(`/pages/${id}`, data);
      setDoc(r.data); setData(r.data.data);
      toast.success("Draft saved — live page unchanged until publish");
      return true;
    } catch (e) { toast.error(fmtErr(e)); return false; } finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await adminApi.put(`/pages/${id}`, data);
      const r = await adminApi.post(`/pages/${id}/publish`);
      setDoc(r.data);
      toast.success(`Published — live at /p/${id}`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const unpublish = async () => {
    setBusy("unpublish");
    try {
      const r = await adminApi.post(`/pages/${id}/unpublish`);
      setDoc(r.data);
      toast.success("Page unpublished");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const doDelete = async () => {
    setBusy("delete");
    try {
      await adminApi.delete(`/pages/${id}`);
      toast.success("Page deleted");
      navigate("/admin/pages");
    } catch (e) { toast.error(fmtErr(e)); setBusy(""); }
  };

  const restore = async (idx) => {
    try {
      const r = await adminApi.post(`/pages/${id}/revisions/${idx}/restore`);
      setDoc(r.data); setData(r.data.data); setRevOpen(false);
      toast.success("Revision restored into draft");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="page-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/pages" className="p-2 text-slate-400 hover:text-slate-700"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900">{data.title || "Untitled Page"}</h1>
            <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5"><StatusChip status={doc.status} /><span className="font-mono-tech">/p/{id}</span></p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {doc.status === "published" && <a href={`/p/${id}`} target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F] px-3">View Live</a>}
          <Btn variant="ghost" onClick={save} disabled={!!busy} data-testid="page-save-draft-btn">{busy === "save" && <Loader2 size={13} className="animate-spin" />} Save Draft</Btn>
          <Btn onClick={publish} disabled={!!busy} data-testid="page-publish-btn">{doc.status === "published" ? "Update Live Version" : "Publish"}</Btn>
          {doc.status === "published" && <Btn variant="dark" onClick={unpublish} disabled={!!busy}>Unpublish</Btn>}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <Field label="Page Title *"><input className={inputCls} value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="page-field-title" /></Field>
            <Field label="Subtitle" hint="Shown under the title on the page.">
              <input className={inputCls} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="page-field-subtitle" />
            </Field>
          </div>
          {data.blocks.map((b, i) => (
            <BlockEditor key={i} block={b} index={i} onChange={setBlock} onMove={moveBlock}
              onRemove={(k) => set("blocks", data.blocks.filter((_, x) => x !== k))}
              openPicker={(k) => setPickerIdx(k)} />
          ))}
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3">Add Block</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(BLOCK_META).map(([type, m]) => (
                <Btn key={type} variant="ghost" onClick={() => set("blocks", [...data.blocks, newBlock(type)])} data-testid={`add-block-${type}`}>
                  <m.icon size={13} /> {m.label}
                </Btn>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <Field label="SEO Title"><input className={inputCls} value={data.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} maxLength={200} data-testid="page-field-seo-title" /></Field>
            <Field label="Meta Description"><textarea rows={3} className={inputCls} value={data.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} maxLength={400} data-testid="page-field-seo-desc" /></Field>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
            <button onClick={() => setRevOpen(true)} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F]" data-testid="page-revisions-btn">
              <History size={14} /> Revision History ({doc.revisions?.length || 0})
            </button>
            {user.role === "super_admin" && (
              <button onClick={() => setDelOpen(true)} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-500 hover:text-red-700" data-testid="delete-page-btn">
                <Trash2 size={14} /> Delete Page
              </button>
            )}
          </div>
        </div>
      </div>

      <MediaPicker open={pickerIdx !== null} onClose={() => setPickerIdx(null)} onSelect={(url) => setBlock(pickerIdx, { ...data.blocks[pickerIdx], src: url })} />
      <ConfirmDialog open={delOpen} title="Delete this page?" message={`"${data.title}" will be permanently removed. This cannot be undone.`} onConfirm={doDelete} onClose={() => setDelOpen(false)} busy={busy === "delete"} />
      <Modal open={revOpen} onClose={() => setRevOpen(false)} title="Revision History">
        {doc.revisions?.length ? (
          <ul className="space-y-3">
            {doc.revisions.map((r, i) => (
              <li key={i} className="flex items-center justify-between border border-slate-200 px-4 py-3 rounded-sm">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{r.data?.title}</p>
                  <p className="text-xs text-slate-400">Published {r.at?.slice(0, 16).replace("T", " ")} by {r.by}</p>
                </div>
                <Btn variant="ghost" onClick={() => restore(i)} data-testid={`page-restore-${i}`}>Restore</Btn>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No previous versions yet.</p>
        )}
      </Modal>
    </div>
  );
};

export default PageEditor;
